import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import PrivateRoute from './components/layout/PrivateRoute';
import MainLayout from './components/layout/MainLayout';
import { useSettings } from './context/SettingsContext';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import DispatchList from './pages/DispatchList';
import DispatchForm from './pages/DispatchForm';
import DispatchView from './pages/DispatchView';
import DispatchPrint from './pages/DispatchPrint';
import CaseHistoryPrint from './pages/CaseHistoryPrint';
import ReceiptList from './pages/ReceiptList';
import ReceiptForm from './pages/ReceiptForm';
import ReceiptView from './pages/ReceiptView';
import ComplianceList from './pages/ComplianceList';
import SearchPage from './pages/SearchPage';
import AuditLog from './pages/AuditLog';
import UserManagement from './pages/UserManagement';
import OfficeManagement from './pages/OfficeManagement';
import Profile from './pages/Profile';
import NotificationList from './pages/NotificationList';
import NotFound from './pages/NotFound';
import Settings from './pages/Settings';
import DigitalGreenSheet from './pages/DigitalGreenSheet';
import Approvals from './pages/Approvals';
import FileWorkspace from './pages/FileWorkspace';
import EFileWorkspace from './pages/EFile/FileWorkspace';
import MyDeskKanban from './pages/MyDeskKanban';
import RecordRoom from './pages/RecordRoom';
import DelegationManager from './pages/DelegationManager';
import AddressBook from './pages/AddressBook/AddressBook';
import Reports from './pages/Reports';

const EfileGuard = ({ children }) => {
  const { settings, loading } = useSettings();
  const enabled = settings?.efile_enabled == 1 || settings?.efile_enabled === 'true' || settings?.efile_enabled === true;

  if (loading) {
    return <div className="p-6 text-gray-500">Loading...</div>;
  }

  if (!enabled) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      
      {/* Protected Routes */}
      <Route element={<PrivateRoute />}>
        <Route element={<MainLayout />}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/dashboard" element={<Dashboard />} />
          
          {/* Workspace Routes */}
          <Route path="/files" element={<EfileGuard><FileWorkspace /></EfileGuard>} />
          <Route path="/efile/:id" element={<EfileGuard><EFileWorkspace /></EfileGuard>} />
          <Route path="/kanban" element={<EfileGuard><MyDeskKanban /></EfileGuard>} />
          <Route path="/record-room" element={<EfileGuard><RecordRoom /></EfileGuard>} />
          <Route path="/delegations" element={<EfileGuard><DelegationManager /></EfileGuard>} />
          <Route path="/address-book" element={<AddressBook />} />

          {/* Dispatch Routes */}
          <Route path="/dispatch" element={<DispatchList />} />
          <Route path="/dispatch/new" element={<DispatchForm />} />
          <Route path="/dispatch/:id/view" element={<DispatchView />} />
          <Route path="/dispatch/:id" element={<DispatchForm />} />
          
          {/* Diary Routes */}
          <Route path="/diary" element={<ReceiptList />} />
          <Route path="/diary/new" element={<ReceiptForm />} />
          <Route path="/diary/:id/view" element={<ReceiptView />} />
          <Route path="/diary/:id" element={<ReceiptForm />} />
          <Route path="/green-sheet/:id" element={<DigitalGreenSheet />} />
          
          {/* Compliance Route */}
          <Route path="/compliance" element={<ComplianceList />} />

          {/* Approvals Route */}
          <Route path="/approvals" element={<Approvals />} />
          
          {/* Search Route */}
          <Route path="/search" element={<SearchPage />} />

          {/* Reports Route */}
          <Route path="/reports" element={<Reports />} />
          
          {/* Profile & Notifications */}
          <Route path="/profile" element={<Profile />} />
          <Route path="/profile/:id" element={<Profile />} />
          <Route path="/notifications" element={<NotificationList />} />
          
          {/* Admin Routes */}
          <Route path="/users" element={<UserManagement />} />
          <Route path="/audit" element={<AuditLog />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/offices" element={<OfficeManagement />} />
        </Route>
      </Route>

      {/* Print Routes (No Layout) */}
      <Route element={<PrivateRoute />}>
         <Route path="/dispatch/:id/print" element={<DispatchPrint />} />
         <Route path="/print/history/:caseType/:caseId" element={<CaseHistoryPrint />} />
      </Route>
      
      {/* Fallback */}
      <Route path="/404" element={<NotFound />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
}

export default App;
